import React from 'react';
import { Link } from 'react-router-dom';
import Background from '../components/Background';

const TermsAndConditions: React.FC = () => {
  return (
    <div className="min-h-screen relative overflow-hidden" style={{ backgroundColor: '#050508' }}>
      <Background />
      
      <div className="relative z-10 min-h-screen flex flex-col items-center py-8 md:py-12 px-4 sm:px-6">
        <div className="max-w-4xl w-full">
          {/* Header */}
          <div className="text-center mb-8 md:mb-12">
            <Link 
              to="/" 
              className="inline-block mb-6 text-cosmic-gold hover:text-cosmic-gold/80 transition-colors duration-300 font-elegant text-sm tracking-luxury-tight"
            >
              ← Back to Home
            </Link>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-heading font-bold text-gradient-gold mb-4 tracking-luxury">
              Terms and Conditions
            </h1>
            <p className="text-sm text-text-secondary/70 font-sans">
              Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </div>

          {/* Content */}
          <div className="glass-grain rounded-2xl p-6 md:p-8 lg:p-10 backdrop-blur-xl border border-cosmic-gold/20 space-y-6 md:space-y-8">
            <section>
              <h2 className="text-2xl md:text-3xl font-heading text-gradient-gold mb-4 tracking-luxury">
                Agreement to Terms
              </h2>
              <p className="text-sm md:text-base text-text-secondary/80 font-sans leading-relaxed">
                By accessing or using Aivaris ("the Service"), you agree to be bound by these Terms and Conditions ("Terms"). If you disagree with any part of these Terms, you may not access or use the Service.
              </p>
            </section>

            <section>
              <h2 className="text-2xl md:text-3xl font-heading text-gradient-gold mb-4 tracking-luxury">
                Description of Service
              </h2>
              <p className="text-sm md:text-base text-text-secondary/80 font-sans leading-relaxed">
                Aivaris is an AI-powered astrological consultation platform that provides personalized readings based on your birth chart. The Service uses artificial intelligence to generate astrological insights and guidance. Our readings are for entertainment and personal reflection purposes only and should not be considered as professional, medical, legal, or financial advice.
              </p>
            </section>

            <section>
              <h2 className="text-2xl md:text-3xl font-heading text-gradient-gold mb-4 tracking-luxury">
                User Accounts
              </h2>
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg md:text-xl font-heading text-text-primary mb-2">Account Creation</h3>
                  <p className="text-sm md:text-base text-text-secondary/80 font-sans leading-relaxed">
                    To use certain features of the Service, you must create an account. You agree to provide accurate, current, and complete information during registration and to update such information to keep it accurate, current, and complete.
                  </p>
                </div>
                <div>
                  <h3 className="text-lg md:text-xl font-heading text-text-primary mb-2">Account Security</h3>
                  <p className="text-sm md:text-base text-text-secondary/80 font-sans leading-relaxed">
                    You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You agree to notify us immediately of any unauthorized use of your account.
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl md:text-3xl font-heading text-gradient-gold mb-4 tracking-luxury">
                Acceptable Use
              </h2>
              <p className="text-sm md:text-base text-text-secondary/80 font-sans leading-relaxed mb-4">
                You agree not to:
              </p>
              <ul className="list-disc list-inside space-y-2 text-sm md:text-base text-text-secondary/80 font-sans leading-relaxed">
                <li>Use the Service for any illegal purpose or in violation of any laws</li>
                <li>Attempt to gain unauthorized access to the Service or its related systems</li>
                <li>Interfere with or disrupt the Service or servers connected to the Service</li>
                <li>Use automated systems (bots, scrapers) to access the Service without permission</li>
                <li>Impersonate any person or entity or misrepresent your affiliation with any person or entity</li>
                <li>Transmit any viruses, malware, or other harmful code</li>
                <li>Harass, abuse, or harm other users</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl md:text-3xl font-heading text-gradient-gold mb-4 tracking-luxury">
                Intellectual Property
              </h2>
              <p className="text-sm md:text-base text-text-secondary/80 font-sans leading-relaxed mb-4">
                The Service and its original content, features, and functionality are owned by Aivaris and are protected by international copyright, trademark, patent, trade secret, and other intellectual property laws. You may not copy, modify, distribute, sell, or lease any part of our Service without our express written permission.
              </p>
              <p className="text-sm md:text-base text-text-secondary/80 font-sans leading-relaxed">
                The astrological readings generated by the Service are provided for your personal use. You may not reproduce, distribute, or commercially exploit these readings without our permission.
              </p>
            </section>

            <section>
              <h2 className="text-2xl md:text-3xl font-heading text-gradient-gold mb-4 tracking-luxury">
                Disclaimer of Warranties
              </h2>
              <p className="text-sm md:text-base text-text-secondary/80 font-sans leading-relaxed mb-4">
                THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO:
              </p>
              <ul className="list-disc list-inside space-y-2 text-sm md:text-base text-text-secondary/80 font-sans leading-relaxed">
                <li>The accuracy, reliability, or completeness of astrological readings</li>
                <li>The uninterrupted or error-free operation of the Service</li>
                <li>The correction of any defects or errors</li>
                <li>The security of the Service or the absence of viruses or harmful components</li>
              </ul>
              <p className="text-sm md:text-base text-text-secondary/80 font-sans leading-relaxed mt-4">
                Astrological readings are for entertainment and personal reflection purposes only. They should not be used as a substitute for professional advice in areas such as medicine, law, finance, or mental health.
              </p>
            </section>

            <section>
              <h2 className="text-2xl md:text-3xl font-heading text-gradient-gold mb-4 tracking-luxury">
                Limitation of Liability
              </h2>
              <p className="text-sm md:text-base text-text-secondary/80 font-sans leading-relaxed">
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, AIVARIS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, WHETHER INCURRED DIRECTLY OR INDIRECTLY, OR ANY LOSS OF DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES, RESULTING FROM YOUR USE OF THE SERVICE.
              </p>
            </section>

            <section>
              <h2 className="text-2xl md:text-3xl font-heading text-gradient-gold mb-4 tracking-luxury">
                Subscription and Payment Terms
              </h2>
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg md:text-xl font-heading text-text-primary mb-2">Free Tier</h3>
                  <p className="text-sm md:text-base text-text-secondary/80 font-sans leading-relaxed">
                    We offer a free tier that includes limited access to the Service. The specific features and limitations of the free tier are subject to change at our discretion.
                  </p>
                </div>
                <div>
                  <h3 className="text-lg md:text-xl font-heading text-text-primary mb-2">Paid Subscriptions</h3>
                  <p className="text-sm md:text-base text-text-secondary/80 font-sans leading-relaxed">
                    If you purchase a paid subscription, you agree to pay all fees associated with your subscription. Subscription fees are billed in advance and are non-refundable except as required by law or as otherwise stated in our refund policy.
                  </p>
                </div>
                <div>
                  <h3 className="text-lg md:text-xl font-heading text-text-primary mb-2">Cancellation</h3>
                  <p className="text-sm md:text-base text-text-secondary/80 font-sans leading-relaxed">
                    You may cancel your subscription at any time. Cancellation will take effect at the end of your current billing period. You will continue to have access to paid features until the end of your billing period.
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl md:text-3xl font-heading text-gradient-gold mb-4 tracking-luxury">
                Termination
              </h2>
              <p className="text-sm md:text-base text-text-secondary/80 font-sans leading-relaxed">
                We may terminate or suspend your account and access to the Service immediately, without prior notice or liability, for any reason, including if you breach these Terms. Upon termination, your right to use the Service will cease immediately.
              </p>
            </section>

            <section>
              <h2 className="text-2xl md:text-3xl font-heading text-gradient-gold mb-4 tracking-luxury">
                Changes to Terms
              </h2>
              <p className="text-sm md:text-base text-text-secondary/80 font-sans leading-relaxed">
                We reserve the right to modify or replace these Terms at any time. If a revision is material, we will provide at least 30 days' notice prior to any new terms taking effect. What constitutes a material change will be determined at our sole discretion. Your continued use of the Service after any changes constitutes acceptance of the new Terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl md:text-3xl font-heading text-gradient-gold mb-4 tracking-luxury">
                Governing Law
              </h2>
              <p className="text-sm md:text-base text-text-secondary/80 font-sans leading-relaxed">
                These Terms shall be governed by and construed in accordance with the laws of the jurisdiction in which Aivaris operates, without regard to its conflict of law provisions. Any disputes arising from these Terms or your use of the Service shall be resolved in the appropriate courts of that jurisdiction.
              </p>
            </section>

            <section>
              <h2 className="text-2xl md:text-3xl font-heading text-gradient-gold mb-4 tracking-luxury">
                Contact Information
              </h2>
              <p className="text-sm md:text-base text-text-secondary/80 font-sans leading-relaxed">
                If you have any questions about these Terms and Conditions, please contact us at:
              </p>
              <p className="text-sm md:text-base text-cosmic-gold font-sans mt-2">
                Email: kaashika2599@gmail.com
              </p>
            </section>
          </div>

          {/* Footer */}
          <div className="text-center mt-8 md:mt-12">
            <Link 
              to="/" 
              className="inline-block text-cosmic-gold hover:text-cosmic-gold/80 transition-colors duration-300 font-elegant text-sm tracking-luxury-tight"
            >
              ← Back to Home
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsAndConditions;

